import React, { useState, useEffect } from 'react';
import { 
  X, 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Plus,
  Car,
  Calendar,
  Gauge,
  Fuel,
  Settings,
  Palette,
  FileText,
  Star,
  AlertCircle,
  Check
} from 'lucide-react';
import { vehicleService } from '../services/api';

export const VehicleModal = ({ isOpen, onClose, onSubmit, editingVehicle = null }) => {
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    fuel_type: 'flex',
    transmission: 'manual',
    color: 'Branco',
    status: 'disponivel',
    description: '',
    features: [],
    is_featured: false
  });

  const [images, setImages] = useState([]);
  const [newFeature, setNewFeature] = useState('');
  const [featureSuggestions, setFeatureSuggestions] = useState([]);
  const [showFeatureSuggestions, setShowFeatureSuggestions] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Preencher formulário quando estiver editando
  useEffect(() => {
    if (editingVehicle) {
      setFormData({
        brand: editingVehicle.brand || '',
        model: editingVehicle.model || '',
        year: editingVehicle.year || new Date().getFullYear(),
        price: editingVehicle.price || '',
        mileage: editingVehicle.mileage || '',
        fuel_type: editingVehicle.fuel_type || 'flex',
        transmission: editingVehicle.transmission || 'manual',
        color: editingVehicle.color || 'Branco',
        status: editingVehicle.status || 'disponivel',
        description: editingVehicle.description || '',
        features: editingVehicle.features || [],
        is_featured: editingVehicle.is_featured || false
      });
    } else {
      // Resetar formulário para novo veículo
      setFormData({
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        price: '',
        mileage: '',
        fuel_type: 'flex',
        transmission: 'manual',
        color: 'Branco',
        status: 'disponivel',
        description: '',
        features: [],
        is_featured: false
      });
    }
    setImages([]);
    setErrors({});
    setSubmitError('');
  }, [editingVehicle, isOpen]);

  // Carregar sugestões de características quando o modal for aberto
  useEffect(() => {
    const fetchFeatureSuggestions = async () => {
      try {
        const response = await vehicleService.getFeatureSuggestions();
        if (response && response.success) {
          setFeatureSuggestions(response.data || []);
        } else {
          setFeatureSuggestions([]);
        }
      } catch (error) {
        console.error('Erro ao carregar características sugeridas:', error);
        setFeatureSuggestions([]);
      }
    };

    if (isOpen) {
      fetchFeatureSuggestions();
    }
  }, [isOpen]);

  const fuelTypes = [
    { value: 'gasolina', label: 'Gasolina' },
    { value: 'alcool', label: 'Álcool' },
    { value: 'flex', label: 'Flex' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'eletrico', label: 'Elétrico' },
    { value: 'hibrido', label: 'Híbrido' }
  ];

  const transmissions = [
    { value: 'manual', label: 'Manual' },
    { value: 'automatico', label: 'Automático' },
    { value: 'cvt', label: 'CVT' },
    { value: 'semi-automatico', label: 'Semi-automático' }
  ];

  const statusOptions = [
    { value: 'disponivel', label: 'Disponível' },
    { value: 'vendido', label: 'Vendido' },
    { value: 'reservado', label: 'Reservado' },
    { value: 'indisponivel', label: 'Indisponível' }
  ];

  const colors = [
    'Branco', 'Preto', 'Prata', 'Cinza', 'Azul', 'Vermelho', 
    'Verde', 'Amarelo', 'Marrom', 'Dourado', 'Outro'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const addFeature = () => {
    const trimmed = newFeature.trim();
    if (trimmed && !formData.features.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, trimmed]
      }));
      setNewFeature('');
    }
  };

  const addFeatureFromSuggestion = (feature) => {
    if (!feature) return;
    if (!formData.features.includes(feature)) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, feature]
      }));
    }
    setNewFeature('');
    setShowFeatureSuggestions(false);
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.brand.trim()) newErrors.brand = 'Marca é obrigatória';
    if (!formData.model.trim()) newErrors.model = 'Modelo é obrigatório';
    if (!formData.year || formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      newErrors.year = 'Ano inválido';
    }
    if (!formData.price || formData.price <= 0) newErrors.price = 'Preço é obrigatório';
    if (!formData.mileage || formData.mileage < 0) newErrors.mileage = 'Quilometragem é obrigatória';
    if (!formData.description.trim()) newErrors.description = 'Descrição é obrigatória';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Preparar FormData para envio com arquivos
      const formDataToSend = new FormData();
      
      // Adicionar dados do veículo
      formDataToSend.append('brand', formData.brand);
      formDataToSend.append('model', formData.model);
      formDataToSend.append('year', formData.year);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('mileage', formData.mileage);
      formDataToSend.append('fuel_type', formData.fuel_type);
      formDataToSend.append('transmission', formData.transmission);
      formDataToSend.append('color', formData.color);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('is_featured', formData.is_featured);
      formDataToSend.append('features', JSON.stringify(formData.features));
      
      // Adicionar arquivos de imagem
      images.forEach((image, index) => {
        formDataToSend.append('images', image.file);
      });

      await onSubmit(formDataToSend);
      
      // Resetar formulário apenas se não estiver editando
      if (!editingVehicle) {
        setFormData({
          brand: '',
          model: '',
          year: new Date().getFullYear(),
          price: '',
          mileage: '',
          fuel_type: 'flex',
          transmission: 'manual',
          color: 'Branco',
          status: 'disponivel',
          description: '',
          features: [],
          is_featured: false
        });
        setImages([]);
      }
      setErrors({});
      setSubmitError('');
      onClose();
      
    } catch (error) {
      console.error('Erro ao cadastrar veículo:', error);
      setSubmitError(error.message || 'Erro ao cadastrar veículo. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Limpar imagens da memória
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    setErrors({});
    setSubmitError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-dark-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Car className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingVehicle ? 'Editar Veículo' : 'Cadastrar Novo Veículo'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {editingVehicle ? 'Atualize as informações do veículo' : 'Preencha as informações do veículo'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Erro de submit */}
          {submitError && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 dark:text-red-300 text-sm">{submitError}</span>
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna Esquerda */}
            <div className="space-y-6">
              {/* Informações Básicas */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Informações Básicas
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Marca *
                      </label>
                      <input
                        type="text"
                        name="brand"
                        value={formData.brand}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          errors.brand ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'
                        }`}
                        placeholder="Ex: Toyota"
                      />
                      {errors.brand && (
                        <p className="text-red-500 text-sm mt-1">{errors.brand}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Modelo *
                      </label>
                      <input
                        type="text"
                        name="model"
                        value={formData.model}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          errors.model ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'
                        }`}
                        placeholder="Ex: Corolla"
                      />
                      {errors.model && (
                        <p className="text-red-500 text-sm mt-1">{errors.model}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ano *
                      </label>
                      <input
                        type="number"
                        name="year"
                        value={formData.year}
                        onChange={handleChange}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          errors.year ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'
                        }`}
                      />
                      {errors.year && (
                        <p className="text-red-500 text-sm mt-1">{errors.year}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Quilometragem *
                      </label>
                      <input
                        type="number"
                        name="mileage"
                        value={formData.mileage}
                        onChange={handleChange}
                        min="0"
                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          errors.mileage ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'
                        }`}
                        placeholder="Ex: 50000"
                      />
                      {errors.mileage && (
                        <p className="text-red-500 text-sm mt-1">{errors.mileage}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Preço *
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                        errors.price ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'
                      }`}
                      placeholder="Ex: 85000.00"
                    />
                    {errors.price && (
                      <p className="text-red-500 text-sm mt-1">{errors.price}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Especificações */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Especificações
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Combustível
                      </label>
                      <select
                        name="fuel_type"
                        value={formData.fuel_type}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {fuelTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Câmbio
                      </label>
                      <select
                        name="transmission"
                        value={formData.transmission}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {transmissions.map(trans => (
                          <option key={trans.value} value={trans.value}>
                            {trans.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Cor
                      </label>
                      <select
                        name="color"
                        value={formData.color}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {colors.map(color => (
                          <option key={color} value={color}>
                            {color}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {statusOptions.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.description ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'
                  }`}
                  placeholder="Descreva o veículo, histórico, estado de conservação..."
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>
            </div>

            {/* Coluna Direita */}
            <div className="space-y-6">
              {/* Upload de Imagens */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Imagens do Veículo
                </h3>
                
                <div className="space-y-4">
                  {/* Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-lg p-6 text-center hover:border-primary transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center space-y-2"
                    >
                      <Upload className="h-8 w-8 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Clique para adicionar imagens
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-500">
                        PNG, JPG até 10MB cada
                      </span>
                    </label>
                  </div>

                  {/* Preview das Imagens */}
                  {images.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image.preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Características */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Características
                </h3>
                
                <div className="space-y-4">
                  {/* Adicionar Característica */}
                  <div className="relative">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newFeature}
                        onChange={(e) => {
                          setNewFeature(e.target.value);
                          setShowFeatureSuggestions(true);
                        }}
                        onFocus={() => setShowFeatureSuggestions(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addFeature();
                            setShowFeatureSuggestions(false);
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Ex: Ar condicionado"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          addFeature();
                          setShowFeatureSuggestions(false);
                        }}
                        className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Sugestões de características */}
                    {showFeatureSuggestions && featureSuggestions.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg">
                        {featureSuggestions
                          .filter((feature) => {
                            const alreadySelected = formData.features.includes(feature);
                            const matchesSearch = newFeature
                              ? feature.toLowerCase().includes(newFeature.toLowerCase())
                              : true;
                            return !alreadySelected && matchesSearch;
                          })
                          .map((feature) => (
                            <button
                              key={feature}
                              type="button"
                              onClick={() => addFeatureFromSuggestion(feature)}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700"
                            >
                              {feature}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Lista de Características */}
                  {formData.features.length > 0 && (
                    <div className="space-y-2">
                      {formData.features.map((feature, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-dark-700 rounded-lg px-3 py-2">
                          <div className="flex items-center space-x-2">
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {feature}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Opções Adicionais */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Opções
                </h3>
                
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                    />
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Marcar como destaque
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-dark-700">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{editingVehicle ? 'Atualizando...' : 'Cadastrando...'}</span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  <span>{editingVehicle ? 'Atualizar Veículo' : 'Cadastrar Veículo'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
